/**
 * HelpProvider: mounted once at app level (inside the router, query client and auth).
 *
 * - Loads the signed-in person's tour progress and per-app help settings once, and whether Ask AI is
 *   switched on.
 * - Knows the page on screen (its PageHelpButton registers it) and the tab open on it.
 * - Runs one tour at a time (TourRunner). Done records Completed and Skip records Dismissed for that tour
 *   and version. Skip on a tour that opened by itself also switches off auto-start for every tour of its
 *   app (saved on the server, so on every device); the ? menu switch turns it back on. Admin previews
 *   record nothing.
 * - Never loses a Skip or Done: every change is written to this device first (localStorage
 *   `ccms.help.v1.{userId}`, see helpLocalStore.ts) and queued until the server confirms it. Queued writes
 *   are retried once progress and settings have loaded and whenever the browser comes back online. The
 *   server stays the source of truth across devices (newest change per key wins).
 * - Auto-start: a page's active AutoStart tour opens by itself (once its anchors exist, once per app
 *   session, never loops) only when progress AND settings loaded, the app's tours are not switched off and
 *   the person has no progress row for the tour at all (Completed or Dismissed, any version, on the server
 *   or only on this device). A newer version shows as "Updated" in the ? menu instead.
 * - "Navigate then start": Show me how opens a route, then starts the tour when its first target
 *   appears (gives up after about 8 seconds and shows a centred step instead).
 * - Owns the help panel (HelpDrawer) and the Ask AI conversation (last 5 questions).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useHelpService,
  HelpTourProgressStatusIds,
  type HelpAppPreferenceView,
  type HelpPageView,
  type HelpProgressView,
  type HelpTourView,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  HelpContext,
  type ActiveTourRun,
  type FinishTourOptions,
  type HelpAskEntry,
  type HelpAskInput,
  type HelpContextValue,
  type HelpDrawerState,
  type StartTourOptions,
} from './HelpContext';
import { TourRunner } from './TourRunner';
import { HelpDrawer } from './HelpDrawer';
import { HELP_STALE_MS, appKeyOfPageKey, friendlyAskError, helpAppTitle, helpErrorMessage, helpKeys } from './helpUtils';
import {
  combineHelpLocal,
  confirmAppWrite,
  confirmProgressWrite,
  dropHelpWrite,
  dropOvertakenWrites,
  emptyHelpLocalState,
  forgetHelpTours,
  helpStorageKey,
  mergedApps,
  mergedProgress,
  pendingKey,
  progressViewOf,
  appViewOf,
  queueHelpWrite,
  readHelpLocal,
  reconcileHelpLocal,
  writeHelpLocal,
  type HelpAppWrite,
  type HelpLocalState,
  type HelpPendingWrite,
  type HelpProgressWrite,
} from './helpLocalStore';

/** Right that lets non-admins edit help content (PG identity/008). */
export const HELP_CONTENT_MANAGE_RIGHT = 'Help.Content.Manage';

const MAX_CONVERSATION = 5;
const FIRST_STEP_WAIT_MS = 8000;
const AUTO_START_DELAY_MS = 800;

/** This device's copy and whose it is (null = signed out or the user id is not known yet: memory only). */
interface LocalCopy {
  userId: string | null;
  state: HelpLocalState;
}

function loadLocal(userId: string | null): HelpLocalState {
  return (userId ? readHelpLocal(userId) : null) ?? emptyHelpLocalState();
}

/** The server refused the write for good (bad request, or the tour or app no longer has help): stop retrying. */
function isPermanentFailure(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 400 || status === 404 || status === 422;
}

const nowIso = () => new Date().toISOString();

export function HelpProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, hasRole, hasPermission, user } = useEnrichedAuth();
  const help = useHelpService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const locationRef = useRef(location);
  locationRef.current = location;

  const isAdmin = hasRole('Admin') || hasRole('admin');
  const canManageHelp = isAdmin || hasPermission(HELP_CONTENT_MANAGE_RIGHT);
  const ready = !!isAuthenticated;
  const userId = ready && user?.id ? String(user.id) : null;

  // ------------------------------------------------------------------ this device's copy

  const [local, setLocal] = useState<LocalCopy>(() => ({ userId, state: loadLocal(userId) }));
  const localRef = useRef(local);
  localRef.current = local;
  /** False after a failed localStorage write: keep building on memory until a write works again. */
  const storageOkRef = useRef(true);

  // Switch to the signed-in person's copy. Changes made while the user id was not known yet are kept.
  useEffect(() => {
    const prev = localRef.current;
    if (prev.userId === userId) return;
    let state = loadLocal(userId);
    if (userId && prev.userId === null && prev.state.pending.length > 0) {
      state = combineHelpLocal(state, prev.state);
      storageOkRef.current = writeHelpLocal(userId, state);
    }
    const next = { userId, state };
    localRef.current = next;
    setLocal(next);
  }, [userId]);

  const localReady = !!userId && local.userId === userId;

  /** Read, change, write: starts from the stored copy so changes made in another tab are kept. */
  const mutateLocal = useCallback((change: (s: HelpLocalState) => HelpLocalState): HelpLocalState => {
    const uid = localRef.current.userId;
    const stored = uid && storageOkRef.current ? readHelpLocal(uid) : null;
    const next = change(stored ?? localRef.current.state);
    if (uid) storageOkRef.current = writeHelpLocal(uid, next);
    const value = { userId: uid, state: next };
    localRef.current = value;
    setLocal(value);
    return next;
  }, []);

  // Another tab of the same person changed the copy: take it over.
  useEffect(() => {
    if (!userId) return undefined;
    const key = helpStorageKey(userId);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || localRef.current.userId !== userId) return;
      const fresh = readHelpLocal(userId);
      if (!fresh) return;
      const value = { userId, state: fresh };
      localRef.current = value;
      setLocal(value);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  // ------------------------------------------------------------------ progress, settings + ask status

  const progressKey = useMemo(() => helpKeys.progress(userId), [userId]);
  const preferencesKey = useMemo(() => helpKeys.preferences(userId), [userId]);
  const progressKeyRef = useRef(progressKey);
  progressKeyRef.current = progressKey;
  const preferencesKeyRef = useRef(preferencesKey);
  preferencesKeyRef.current = preferencesKey;

  const progressQuery = useQuery({
    queryKey: progressKey,
    queryFn: () => help.getMyHelpProgress(),
    enabled: ready,
    staleTime: Infinity,
    retry: 1,
  });
  const preferencesQuery = useQuery({
    queryKey: preferencesKey,
    queryFn: () => help.getMyHelpPreferences(),
    enabled: ready,
    staleTime: Infinity,
    retry: 1,
  });
  // A failed load must not block help: treat it as nothing stored on the server, but never auto-start.
  const progressLoaded = progressQuery.isSuccess;
  const preferencesLoaded = preferencesQuery.isSuccess;
  const serverProgress = progressQuery.isSuccess ? progressQuery.data : undefined;
  const serverApps = preferencesQuery.isSuccess ? preferencesQuery.data : undefined;

  const progress = useMemo(() => new Map(Object.entries(mergedProgress(serverProgress, local.state))), [serverProgress, local.state]);
  const appPreferences = useMemo(() => new Map(Object.entries(mergedApps(serverApps, local.state))), [serverApps, local.state]);

  const appAutoStart = useCallback((appKey: string) => appPreferences.get(appKey)?.autoStartTours ?? true, [appPreferences]);

  const askStatusQuery = useQuery({
    queryKey: helpKeys.askStatus(),
    queryFn: () => help.getHelpAskStatus(),
    enabled: ready,
    staleTime: HELP_STALE_MS,
    retry: 0,
  });
  const ask = useMemo(
    () => ({
      enabled: !!askStatusQuery.data?.enabled,
      message: askStatusQuery.data?.message ?? null,
      loaded: askStatusQuery.isSuccess || askStatusQuery.isError,
    }),
    [askStatusQuery.data, askStatusQuery.isSuccess, askStatusQuery.isError]
  );

  // ------------------------------------------------------------------ saving (this device first, then the server)

  /** Writes on their way to the server, by key: one request per key at a time. */
  const inFlightRef = useRef(new Set<string>());

  const sendWrite = useCallback(
    async (write: HelpPendingWrite): Promise<void> => {
      const key = pendingKey(write);
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      let settled = false;
      try {
        if (write.kind === 'progress') {
          const saved = await help.setMyHelpProgress(write.tourKey, { version: write.version, statusID: write.statusID });
          const view: HelpProgressView = { ...progressViewOf(write), ...saved };
          // Only into loaded data: seeding an empty list would look like a successful load.
          queryClient.setQueryData<HelpProgressView[]>(progressKeyRef.current, (old) => (old ? [...old.filter((p) => p.tourKey !== view.tourKey), view] : old));
          mutateLocal((s) => confirmProgressWrite(s, write, view));
        } else {
          const saved = await help.setMyHelpAppPreference(write.appKey, { autoStartTours: write.autoStartTours });
          const view: HelpAppPreferenceView = { ...appViewOf(write), ...saved };
          queryClient.setQueryData<HelpAppPreferenceView[]>(preferencesKeyRef.current, (old) => (old ? [...old.filter((a) => a.appKey !== view.appKey), view] : old));
          mutateLocal((s) => confirmAppWrite(s, write, view));
        }
        settled = true;
      } catch (err) {
        if (isPermanentFailure(err)) {
          mutateLocal((s) => dropHelpWrite(s, write));
          settled = true;
          console.warn('[Help] The server refused a help change; not retrying it', write, err);
        } else {
          // Kept on this device (so the tour does not open again here) and retried later.
          console.warn('[Help] Could not save a help change yet; it is kept on this device and retried', write, err);
        }
      } finally {
        inFlightRef.current.delete(key);
      }
      if (!settled) return;
      // A newer change for the same key may have been queued while this one was on its way.
      const newer = localRef.current.state.pending.find((p) => pendingKey(p) === key && p.at !== write.at);
      if (newer) void sendWrite(newer);
    },
    [help, queryClient, mutateLocal]
  );

  const flushPending = useCallback(() => {
    localRef.current.state.pending.forEach((w) => void sendWrite(w));
  }, [sendWrite]);

  /** The person whose queued writes were checked against the server and retried after the first full load. */
  const flushedForRef = useRef<string | null>(null);

  // Keep this device's copy in step with the server, and retry queued writes once both lists have loaded.
  useEffect(() => {
    if (!localReady || (!serverProgress && !serverApps)) return;
    const firstFullLoad = flushedForRef.current !== userId ? (serverProgress && serverApps ? { progress: serverProgress, apps: serverApps } : null) : null;
    const next = mutateLocal((s) => reconcileHelpLocal(firstFullLoad ? dropOvertakenWrites(s, firstFullLoad.progress, firstFullLoad.apps) : s, serverProgress, serverApps));
    if (firstFullLoad) {
      flushedForRef.current = userId;
      next.pending.forEach((w) => void sendWrite(w));
    }
  }, [localReady, userId, serverProgress, serverApps, mutateLocal, sendWrite]);

  // Back online: load what failed to load (auto-start waits for it) and retry what is still queued.
  useEffect(() => {
    if (!ready) return undefined;
    const onOnline = () => {
      for (const key of [progressKeyRef.current, preferencesKeyRef.current]) {
        if (queryClient.getQueryState(key)?.status === 'error') void queryClient.refetchQueries({ queryKey: key, exact: true });
      }
      // Before the first full load the load itself retries (after checking for newer changes on the server).
      if (flushedForRef.current && flushedForRef.current === localRef.current.userId) flushPending();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [ready, queryClient, flushPending]);

  const saveProgress = useCallback(
    (tour: HelpTourView, statusID: number) => {
      const write: HelpProgressWrite = { kind: 'progress', tourKey: tour.tourKey, version: tour.version, statusID, at: nowIso() };
      mutateLocal((s) => queueHelpWrite(s, write));
      void sendWrite(write);
    },
    [mutateLocal, sendWrite]
  );

  // ------------------------------------------------------------------ page + tab tracking

  const [currentPageKey, setCurrentPageKey] = useState<string | null>(null);
  const [activeTabs, setActiveTabs] = useState<Record<string, string | null>>({});
  const activeTabsRef = useRef(activeTabs);
  activeTabsRef.current = activeTabs;
  /** Pages whose tours were reset (or whose app's tours were switched back on) while open: no auto-start until the page is opened again. */
  const resetPagesRef = useRef(new Set<string>());

  const registerPage = useCallback((pageKey: string) => {
    setCurrentPageKey(pageKey);
    return () => {
      resetPagesRef.current.delete(pageKey);
      setCurrentPageKey((cur) => (cur === pageKey ? null : cur));
    };
  }, []);

  const setActiveTab = useCallback((pageKey: string, tabKey: string | null) => {
    setActiveTabs((cur) => (cur[pageKey] === tabKey ? cur : { ...cur, [pageKey]: tabKey }));
  }, []);

  const activeTabFor = useCallback((pageKey: string | null | undefined) => (pageKey ? activeTabs[pageKey] ?? null : null), [activeTabs]);

  const setAppAutoStart = useCallback(
    (appKey: string, autoStartTours: boolean, options?: { pageKey?: string | null }) => {
      if (!appKey) return;
      const write: HelpAppWrite = { kind: 'app', appKey, autoStartTours, at: nowIso() };
      mutateLocal((s) => queueHelpWrite(s, write));
      // Switched back on from the ? menu: the open page's tour waits until the page is opened again.
      if (autoStartTours && options?.pageKey) resetPagesRef.current.add(options.pageKey);
      void sendWrite(write);
    },
    [mutateLocal, sendWrite]
  );

  // ------------------------------------------------------------------ drawer

  const [drawer, setDrawer] = useState<HelpDrawerState>({ opened: false, pageKey: null, tabKey: null, focusAskNonce: 0 });
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;

  const openHelp = useCallback(
    (pageKey?: string | null, tabKey?: string | null, options?: { focusAsk?: boolean }) => {
      setDrawer((cur) => {
        const key = pageKey ?? currentPageKey ?? cur.pageKey;
        return {
          opened: true,
          pageKey: key,
          tabKey: tabKey !== undefined ? tabKey : key ? activeTabsRef.current[key] ?? null : null,
          focusAskNonce: options?.focusAsk ? cur.focusAskNonce + 1 : cur.focusAskNonce,
        };
      });
    },
    [currentPageKey]
  );

  const closeHelp = useCallback(() => setDrawer((cur) => ({ ...cur, opened: false })), []);

  // The panel follows the person: after "Go to", it shows the new page (the conversation stays).
  useEffect(() => {
    if (!currentPageKey) return;
    setDrawer((cur) => (cur.opened && cur.pageKey !== currentPageKey ? { ...cur, pageKey: currentPageKey, tabKey: activeTabsRef.current[currentPageKey] ?? null } : cur));
  }, [currentPageKey]);

  // Keep the panel's open tab in step with the page's tab.
  useEffect(() => {
    setDrawer((cur) => {
      if (!cur.opened || !cur.pageKey || cur.pageKey !== currentPageKey) return cur;
      const tab = activeTabs[cur.pageKey] ?? null;
      return tab && tab !== cur.tabKey ? { ...cur, tabKey: tab } : cur;
    });
  }, [activeTabs, currentPageKey]);

  // ------------------------------------------------------------------ tours

  const [activeTour, setActiveTour] = useState<ActiveTourRun | null>(null);
  const activeTourRef = useRef(activeTour);
  activeTourRef.current = activeTour;
  const runSeq = useRef(0);
  /** Tours auto-started (or skipped by the guard) in this app session: "{tourKey}@{version}". */
  const autoGuardRef = useRef(new Set<string>());

  const findCachedTour = useCallback(
    (tourKey: string): HelpTourView | null => {
      const cached = queryClient.getQueriesData<HelpPageView | null>({ queryKey: helpKeys.pages() });
      for (const [, page] of cached) {
        const t = page?.tours?.find((x) => x.tourKey === tourKey);
        if (t) return t;
      }
      return null;
    },
    [queryClient]
  );

  /** The tour's app: the cached page's appKey, else the app part of its page key. */
  const appKeyForPage = useCallback(
    (pageKey: string | null | undefined): string | null => {
      if (!pageKey) return null;
      const page = queryClient.getQueryData<HelpPageView | null>(helpKeys.page(pageKey));
      return page?.appKey || appKeyOfPageKey(pageKey);
    },
    [queryClient]
  );

  const startTour = useCallback(
    async (tourOrKey: HelpTourView | string, options?: StartTourOptions): Promise<boolean> => {
      let tour: HelpTourView | null = typeof tourOrKey === 'string' ? findCachedTour(tourOrKey) : tourOrKey;
      if (!tour && typeof tourOrKey === 'string' && options?.pageKey) {
        try {
          const page = await queryClient.fetchQuery({
            queryKey: helpKeys.page(options.pageKey),
            queryFn: () => help.getHelpPage(options.pageKey as string),
            staleTime: HELP_STALE_MS,
          });
          tour = page?.tours?.find((t) => t.tourKey === tourOrKey) ?? null;
        } catch {
          tour = null;
        }
      }
      if (!tour || (tour.steps?.length ?? 0) === 0) {
        if (!options?.auto) toast.info('No tour yet', 'This tour has no steps yet.');
        return false;
      }
      setDrawer((cur) => (cur.opened ? { ...cur, opened: false } : cur));
      runSeq.current += 1;
      setActiveTour({
        runId: runSeq.current,
        tour,
        preview: !!options?.preview,
        auto: !!options?.auto,
        appKey: options?.appKey || appKeyForPage(tour.pageKey || options?.pageKey),
        firstWaitMs: options?.firstWaitMs ?? FIRST_STEP_WAIT_MS,
        startDelayMs: options?.startDelayMs ?? 0,
        startPath: locationRef.current.pathname,
      });
      return true;
    },
    [findCachedTour, queryClient, help, appKeyForPage]
  );

  const finishTour = useCallback(
    (outcome: 'completed' | 'dismissed', options?: FinishTourOptions) => {
      const run = activeTourRef.current;
      setActiveTour(null);
      if (!run) return;
      autoGuardRef.current.add(`${run.tour.tourKey}@${run.tour.version}`);
      if (run.preview) return;
      saveProgress(run.tour, outcome === 'completed' ? HelpTourProgressStatusIds.Completed : HelpTourProgressStatusIds.Dismissed);
      // Skip on a tour that opened by itself: no tour of this app opens by itself again (on every device).
      // A tour the person started, or a link followed from a step, only records Dismissed for that tour.
      if (outcome === 'dismissed' && run.auto && !options?.navigated && run.appKey) {
        setAppAutoStart(run.appKey, false);
        toast.info(`Tours are off for ${helpAppTitle(run.appKey)}`, "They won't open by themselves again. You can start any tour from the ? menu.");
      }
    },
    [saveProgress, setAppAutoStart]
  );

  const abandonTour = useCallback(() => setActiveTour(null), []);

  const showMeHow = useCallback(
    (route: string, tourKey: string, pageKey?: string | null) => {
      const here = locationRef.current.pathname + locationRef.current.search;
      if (route && route !== here && route !== locationRef.current.pathname) navigate(route);
      void startTour(tourKey, { pageKey, appKey: appKeyForPage(pageKey), firstWaitMs: FIRST_STEP_WAIT_MS, startDelayMs: 300 });
    },
    [navigate, startTour, appKeyForPage]
  );

  const tourStatus = useCallback(
    (tour: HelpTourView): 'new' | 'completed' | 'dismissed' | 'updated' => {
      const p = progress.get(tour.tourKey);
      if (!p) return 'new';
      if (p.version < tour.version) return 'updated';
      return p.statusID === HelpTourProgressStatusIds.Completed ? 'completed' : 'dismissed';
    },
    [progress]
  );

  const autoStartIfDue = useCallback(
    (page: HelpPageView) => {
      // Never without the person's progress AND settings: a failed load must not re-open a tour they skipped.
      if (!ready || !localReady || !progressLoaded || !preferencesLoaded || activeTourRef.current || drawerRef.current.opened) return;
      if (resetPagesRef.current.has(page.pageKey)) return;
      const appKey = page.appKey || appKeyOfPageKey(page.pageKey);
      // The person switched off this app's tours (a Skip on a tour that opened by itself, or the ? menu).
      if (!appAutoStart(appKey)) return;
      const tour = (page.tours ?? []).find((t) => t.isActive && t.autoStart && (t.steps?.length ?? 0) > 0) ?? null;
      if (!tour) return;
      // Any progress row (Completed or Dismissed, any version, on the server or only on this device) means it
      // never opens by itself again; a newer version shows as "Updated" in the ? menu instead.
      if (progress.has(tour.tourKey)) return;
      const guard = `${tour.tourKey}@${tour.version}`;
      if (autoGuardRef.current.has(guard)) return;
      autoGuardRef.current.add(guard);
      void startTour(tour, { auto: true, appKey, firstWaitMs: FIRST_STEP_WAIT_MS, startDelayMs: AUTO_START_DELAY_MS });
    },
    [ready, localReady, progressLoaded, preferencesLoaded, appAutoStart, progress, startTour]
  );

  const resetToursForPage = useCallback(
    async (page: HelpPageView) => {
      const tours = page.tours ?? [];
      if (tours.length === 0) return;
      try {
        await Promise.all(tours.map((t) => help.resetMyHelpProgress(t.tourKey)));
        const keys = new Set(tours.map((t) => t.tourKey));
        queryClient.setQueryData<HelpProgressView[]>(progressKeyRef.current, (old) => (old ? old.filter((p) => !keys.has(p.tourKey)) : old));
        mutateLocal((s) => forgetHelpTours(s, keys));
        tours.forEach((t) => autoGuardRef.current.delete(`${t.tourKey}@${t.version}`));
        resetPagesRef.current.add(page.pageKey);
        const appKey = page.appKey || appKeyOfPageKey(page.pageKey);
        if (appAutoStart(appKey)) {
          toast.success('Tours reset', 'They start again the next time you open this page.');
        } else {
          toast.success('Tours reset', `Start them from the ? menu. Tours don't open by themselves in ${helpAppTitle(appKey)} while that is switched off.`);
        }
      } catch (err) {
        toast.error('Could not reset the tours', helpErrorMessage(err, 'Try again in a moment.'));
      }
    },
    [help, queryClient, mutateLocal, appAutoStart]
  );

  // ------------------------------------------------------------------ Ask AI

  const [conversation, setConversation] = useState<HelpAskEntry[]>([]);
  const askSeq = useRef(0);

  const askAi = useCallback(
    (input: HelpAskInput) => {
      const pageKey = input.pageKey ?? (drawerRef.current.opened ? drawerRef.current.pageKey : null) ?? currentPageKey ?? drawerRef.current.pageKey;
      if (!pageKey) return;
      const tabKey = input.tabKey !== undefined ? input.tabKey : activeTabsRef.current[pageKey] ?? null;
      const question = (input.question ?? '').trim();
      if (!question && !input.intent) return;
      askSeq.current += 1;
      const id = askSeq.current;
      setConversation((cur) => [...cur, { id, label: input.label || question, pageKey, tabKey, status: 'loading' as const }].slice(-MAX_CONVERSATION));
      if (input.openPanel) {
        setDrawer((cur) => ({ ...cur, opened: true, pageKey, tabKey }));
      }
      const loc = locationRef.current;
      help
        .askHelp({
          pageKey,
          tabKey,
          currentRoute: `${loc.pathname}${loc.search}`,
          question,
          intent: input.intent ?? null,
        })
        .then((result) => setConversation((cur) => cur.map((e) => (e.id === id ? { ...e, status: 'done', result } : e))))
        .catch((err) => setConversation((cur) => cur.map((e) => (e.id === id ? { ...e, status: 'error', error: friendlyAskError(err) } : e))));
    },
    [currentPageKey, help]
  );

  const clearConversation = useCallback(() => setConversation([]), []);

  // Signing out closes everything.
  useEffect(() => {
    if (ready) return;
    setDrawer((cur) => ({ ...cur, opened: false }));
    setActiveTour(null);
    setConversation([]);
    flushedForRef.current = null;
  }, [ready]);

  // ------------------------------------------------------------------ value

  const value = useMemo<HelpContextValue>(
    () => ({
      ready,
      canManageHelp,
      isAdmin,
      progress,
      progressLoaded,
      appPreferences,
      preferencesLoaded,
      appAutoStart,
      setAppAutoStart,
      ask,
      currentPageKey,
      registerPage,
      activeTabFor,
      setActiveTab,
      drawer,
      openHelp,
      closeHelp,
      activeTour,
      startTour,
      showMeHow,
      finishTour,
      abandonTour,
      autoStartIfDue,
      resetToursForPage,
      tourStatus,
      conversation,
      askAi,
      clearConversation,
    }),
    [
      ready,
      canManageHelp,
      isAdmin,
      progress,
      progressLoaded,
      appPreferences,
      preferencesLoaded,
      appAutoStart,
      setAppAutoStart,
      ask,
      currentPageKey,
      registerPage,
      activeTabFor,
      setActiveTab,
      drawer,
      openHelp,
      closeHelp,
      activeTour,
      startTour,
      showMeHow,
      finishTour,
      abandonTour,
      autoStartIfDue,
      resetToursForPage,
      tourStatus,
      conversation,
      askAi,
      clearConversation,
    ]
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      {ready && <HelpDrawer />}
      {ready && activeTour && <TourRunner key={activeTour.runId} run={activeTour} onFinish={finishTour} onAbandon={abandonTour} />}
    </HelpContext.Provider>
  );
}
